import React, { FC } from 'react';
import { brandA } from '../../../theme/colours';
import { IconSvg, IconSvgProps } from './IconSvg';

/* 
    Make sure there are no repetitions of class names between different icons (naming convention "cls-<number>") in style definitions. 
    Repetitions of class names can cause problems because classes are generated onto a global stylesheet and can affect other SVGs rendered at the same time.

    Unless the fill is specified inline or with classes, the path and g attribute will automatically fill with either
    the fill that is passed in as props from the higher level Icon component, or 'currentColor', which is the body's text colour.

    If an icon SVG has a color defined within itself (at the time of writing, most SVGs are hardcoded with #494646), 
    this needs replacing with the fill color from props if the icon is to change to the colour passed in at higher level.
*/

const ApplicationCompleteIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="done_all_black_24dp" transform="translate(9.748 9.748)">
            <path id="Path_483" d="M0 0h22.5v22.5H0z" fill="none" />
            <path
                id="Path_484"
                d="M16.9 6.912L15.581 5.59l-5.944 5.945 1.322 1.322zm3.979-1.322l-9.92 9.92-3.92-3.91-1.322 1.323 5.242 5.242L22.211 6.912zM.41 12.923l5.242 5.242 1.322-1.322L1.741 11.6z"
                transform="translate(-0.026 -0.348)"
            />
        </g>
    </IconSvg>
);

const BackArrowIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M29.068 14.967H10l8.758-8.758L16.534 4 4 16.534l12.534 12.534 2.209-2.209L10 18.1h19.068z"
            transform="translate(-0.534 -0.534)"
        />
    </IconSvg>
);

const CommentIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="chat_bubble_outline_black_24dp" transform="translate(8.5 8.5)">
            <path id="Path_503" d="M0 0h24v24H0z" fill="none" />
            <path
                id="Path_504"
                d="M20 2H4a2.006 2.006 0 0 0-2 2v18l4-4h14a2.006 2.006 0 0 0 2-2V4a2.006 2.006 0 0 0-2-2zm0 14H6l-2 2V4h16z"
            />
        </g>
    </IconSvg>
);

const CouncilIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path d="M26.909 7.333H31v2.889h-1.364v15.889H31V29H1v-2.889h1.364V10.222H1V7.333h4.091V4.444A1.406 1.406 0 0 1 6.455 3h19.09a1.406 1.406 0 0 1 1.364 1.444zm0 2.889H5.091v15.889h4.091V16h2.727v10.111h2.727V16h2.727v10.111h2.727V16h2.727v10.111h4.091zM7.818 5.889v1.444h16.364V5.889z" />{' '}
    </IconSvg>
);

const CreationIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="add_black_24dp" transform="translate(9 9)">
            <path id="Path_487" d="M0 0h24v24H0z" fill="none" />
            <path id="Path_488" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z" />
        </g>
    </IconSvg>
);

const CrossIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" opacity="0.87" />
        <path
            d="M15.333 2a13.333 13.333 0 1 0 13.334 13.333A13.321 13.321 0 0 0 15.333 2zm0 24A10.667 10.667 0 1 1 26 15.333 10.681 10.681 0 0 1 15.333 26zM20.12 8.667l-4.787 4.787-4.786-4.787-1.88 1.88 4.787 4.787-4.787 4.786 1.88 1.88 4.787-4.787L20.12 22 22 20.12l-4.787-4.787L22 10.547z"
            transform="translate(0.667 0.667)"
        />
    </IconSvg>
);

const DocumentIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M9.333 20.667H20v2.667H9.333zm0-5.333H20V18H9.333zm8-13.334H6.667A2.675 2.675 0 0 0 4 4.667V26a2.663 2.663 0 0 0 2.653 2.667h16.014A2.675 2.675 0 0 0 25.333 26V10zm5.333 24h-16V4.667H16v6.667h6.667z"
            transform="translate(1.333 0.667)"
        />
    </IconSvg>
);

const DonatedIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M6.263 10.524a1.283 1.283 0 0 1 1.316 1.248 9.494 9.494 0 0 1 5.7 1.871h2.855a6.05 6.05 0 0 1 4.412 1.87h4.141a6.616 6.616 0 0 1 5.942 3.557 17.653 17.653 0 0 1-13.837 6.424A19.854 19.854 0 0 1 7.5 23.426a1.312 1.312 0 0 1-1.237.821H2.316A1.283 1.283 0 0 1 1 23V11.772a1.283 1.283 0 0 1 1.316-1.248zm1.317 3.743v6.264l.059.041A16.285 16.285 0 0 0 16.789 23a15.218 15.218 0 0 0 10.309-3.9l.175-.166-.158-.125a4.067 4.067 0 0 0-2.162-.786l-.27-.009H21.9a5.363 5.363 0 0 1 .147 1.249V20.5H10.21V18h8.934l-.045-.1a3.3 3.3 0 0 0-2.753-1.767l-.216-.006h-3.854a6.741 6.741 0 0 0-4.695-1.871zm-2.633-1.248H3.632v8.733h1.315zm12.692-9.263l.461.444.466-.442a3.418 3.418 0 0 1 4.652 0 3.006 3.006 0 0 1 0 4.411l-5.118 4.85-5.113-4.853a3.006 3.006 0 0 1 0-4.411 3.418 3.418 0 0 1 4.652 0zM14.85 5.522a.6.6 0 0 0-.079.795l.076.086L18.1 9.489 21.363 6.4a.6.6 0 0 0 .076-.8l-.076-.087a.685.685 0 0 0-.839-.071l-.092.072L18.1 7.726l-2.321-2.208-.089-.07a.685.685 0 0 0-.839.072z"
            transform="translate(0.6 1.705)"
        />
    </IconSvg>
);

const DownloadIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <g>
            <path fill="none" d="M0 0H32V32H0z" />
        </g>
        <g>
            <path
                d="M5 25.667h18.667V23H5zM23.667 11h-5.334V3h-8v8H5l9.333 9.333z"
                transform="translate(6.667 4) translate(-5 -3)"
            />
        </g>
    </IconSvg>
);

const EmailSentIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="email_black_24dp" transform="translate(9 9)">
            <path id="Path_485" d="M0 0h24v24H0z" fill="none" />
            <path
                id="Path_486"
                d="M22 6a2.006 2.006 0 0 0-2-2H4a2.006 2.006 0 0 0-2 2v12a2.006 2.006 0 0 0 2 2h16a2.006 2.006 0 0 0 2-2zm-2 0l-8 5-8-5zm0 12H4V8l8 5 8-5z"
            />
        </g>
    </IconSvg>
);

const HideIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0zm0 0h32v32H0zm0 0h32v32H0zm0 0h32v32H0z" />
        <path
            d="M15.667 8.333A6.669 6.669 0 0 1 22.333 15a6.471 6.471 0 0 1-.48 2.44l3.893 3.893A15.756 15.756 0 0 0 30.32 15 15.769 15.769 0 0 0 15.653 5a15.527 15.527 0 0 0-5.307.933l2.88 2.88a6.471 6.471 0 0 1 2.441-.48zM2.333 4.693l3.04 3.04.613.613A15.739 15.739 0 0 0 1 15a15.762 15.762 0 0 0 20.507 8.88l.56.56 3.907 3.893 1.693-1.693L4.027 3zm7.373 7.373l2.067 2.067a3.762 3.762 0 0 0-.107.867 4 4 0 0 0 4 4 3.762 3.762 0 0 0 .867-.107L18.6 20.96a6.623 6.623 0 0 1-8.893-8.893zm5.747-1.04l4.2 4.2.027-.213a4 4 0 0 0-4-4z"
            transform="translate(0.333 1)"
        />
    </IconSvg>
);

const IdCheckIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="perm_identity_black_24dp" transform="translate(8.5 8.5)">
            <path id="Path_492" d="M0 0h24v24H0z" fill="none" />
            <path
                id="Subtraction_2"
                d="M5.891 16H0v-3c0-.818.521-2 3-3a14.8 14.8 0 0 1 5-1 14.853 14.853 0 0 1 5 1c2.475 1 3 2.18 3 3v1.441L14.436 16H8.785l-1.449-1.453L5.891 16zM8 11c-2.711 0-5.8 1.287-6 2.006V14h12v-1c-.2-.719-3.307-2-6-2zm0-3a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm0-6a2 2 0 1 0 2 2 2 2 0 0 0-2-2z"
                transform="translate(4.002 4)"
            />
            <path id="Path_495" d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" transform="translate(6.6 7.5)" />
        </g>
    </IconSvg>
);

const IdentityReportIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="fingerprint_black_24dp" transform="translate(8.5 8.5)">
            <path id="Path_490" d="M0 0h24v24H0z" fill="none" />
            <path
                id="Path_491"
                d="M17.81 4.47a.465.465 0 0 1-.23-.06A11.442 11.442 0 0 0 12.01 3a11.441 11.441 0 0 0-5.57 1.41.5.5 0 0 1-.48-.88 12.724 12.724 0 0 1 12.08-.01.506.506 0 0 1-.23.95zM3.5 9.72a.521.521 0 0 1-.29-.09.5.5 0 0 1-.12-.7 10.339 10.339 0 0 1 3.75-3.27 11.543 11.543 0 0 1 10.31-.01A10.378 10.378 0 0 1 20.9 8.9a.5.5 0 0 1-.82.58 9.388 9.388 0 0 0-3.39-2.94 10.537 10.537 0 0 0-9.4.01 9.487 9.487 0 0 0-3.4 2.96.436.436 0 0 1-.39.21zm6.25 12.07a.469.469 0 0 1-.35-.15A10.109 10.109 0 0 1 7.39 19a8.877 8.877 0 0 1-1.05-4.34A5.538 5.538 0 0 1 12 9.27a5.538 5.538 0 0 1 5.66 5.39.5.5 0 1 1-1 0A4.536 4.536 0 0 0 12 10.27a4.536 4.536 0 0 0-4.66 4.39 7.834 7.834 0 0 0 .93 3.85 9.465 9.465 0 0 0 1.85 2.42.513.513 0 0 1 0 .71.548.548 0 0 1-.37.15zm7.17-1.85a5.385 5.385 0 0 1-3.1-.89 5.324 5.324 0 0 1-2.38-4.39.5.5 0 0 1 1 0 4.3 4.3 0 0 0 1.94 3.56 4.385 4.385 0 0 0 2.54.71 6.609 6.609 0 0 0 1.04-.1.5.5 0 0 1 .17.99 6.829 6.829 0 0 1-1.21.12zM14.91 22a.585.585 0 0 1-.13-.02 7.8 7.8 0 0 1-3.72-2.1 7.3 7.3 0 0 1-2.17-5.22 3.016 3.016 0 0 1 3.08-2.94 3.016 3.016 0 0 1 3.08 2.94 2.085 2.085 0 0 0 4.16 0 7.053 7.053 0 0 0-7.25-6.83 7.3 7.3 0 0 0-6.61 4.03 6.433 6.433 0 0 0-.59 2.8 9.937 9.937 0 0 0 .67 3.61.488.488 0 0 1-.29.64.5.5 0 0 1-.64-.29 11.141 11.141 0 0 1-.73-3.96 7.513 7.513 0 0 1 .68-3.24 8.3 8.3 0 0 1 7.51-4.6 8.055 8.055 0 0 1 8.25 7.83 3.083 3.083 0 0 1-6.16 0 2.014 2.014 0 0 0-2.08-1.94 2.014 2.014 0 0 0-2.08 1.94 6.3 6.3 0 0 0 1.87 4.51 6.8 6.8 0 0 0 3.27 1.85.5.5 0 0 1 .35.61.488.488 0 0 1-.47.38z"
            />
        </g>
    </IconSvg>
);

const PassportIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 49 49">
        <path id="Rectangle_680" fill="none" d="M0 0H49V49H0z" />
        <g id="passport-svgrepo-com-3" transform="translate(9.566 5.244)">
            <g id="Group_1868">
                <g id="Group_1867" transform="translate(0)">
                    <path
                        id="Path_448"
                        d="M84.192 0H58.255a.786.786 0 0 0-.786.786v36.941a.786.786 0 0 0 .786.786h25.937a3.148 3.148 0 0 0 3.144-3.144V3.144A3.147 3.147 0 0 0 84.192 0zm-22.4 36.941h-2.751V1.572h2.751v35.369zm23.972-1.572a1.574 1.574 0 0 1-1.572 1.572H63.364V1.572h20.828a1.574 1.574 0 0 1 1.572 1.572z"
                        transform="translate(-57.469)"
                    />
                    <path
                        id="Path_449"
                        d="M185.005 137.071a8.247 8.247 0 0 0-12.171 4.411 8.194 8.194 0 0 0 0 5.215 8.247 8.247 0 0 0 15.654 0 8.2 8.2 0 0 0 0-5.215 8.237 8.237 0 0 0-3.483-4.411zm-9.06 11.75a6.656 6.656 0 0 1-1.178-1.587h1.178zm0-3.159h-1.777a6.663 6.663 0 0 1 0-3.144h1.777zm0-4.716h-1.178a6.655 6.655 0 0 1 1.178-1.587zm3.93 9.778a6.643 6.643 0 0 1-2.358-.737v-2.753h2.358zm0-5.062h-2.358v-3.144h2.358zm0-4.716h-2.358v-2.753a6.645 6.645 0 0 1 2.358-.737zm3.93 9.041a6.646 6.646 0 0 1-2.358.737v-3.49h2.353zm0-4.325h-2.358v-3.144h2.353zm0-4.716h-2.358v-3.491a6.634 6.634 0 0 1 2.358.738zm1.572-1.587a6.664 6.664 0 0 1 1.178 1.587h-1.178zm0 9.462v-1.587h1.178a6.657 6.657 0 0 1-1.178 1.587zm1.777-3.159h-1.777v-3.144h1.777a6.663 6.663 0 0 1 0 3.144z"
                        transform="translate(-163.762 -125.619)"
                    />
                    <path
                        id="Path_450"
                        d="M205.327 51.736h8.646a1.574 1.574 0 0 0 1.572-1.572v-1.572a1.574 1.574 0 0 0-1.572-1.572h-8.646a1.574 1.574 0 0 0-1.572 1.572v1.572a1.574 1.574 0 0 0 1.572 1.572zm0-3.144h8.646v1.572h-8.646z"
                        transform="translate(-192.751 -43.483)"
                    />
                    <path id="Rectangle_676" d="M0 0H7.074V1.572H0z" transform="translate(12.576 29.474)" />
                    <path id="Rectangle_677" d="M0 0H2.358V1.572H0z" transform="translate(12.576 32.618)" />
                    <path id="Rectangle_678" d="M0 0H2.358V1.572H0z" transform="translate(15.72 32.618)" />
                    <path id="Rectangle_679" d="M0 0H2.358V1.572H0z" transform="translate(18.864 32.618)" />
                </g>
            </g>
        </g>
    </IconSvg>
);

const sharedCardIcon = (
    <>
        <g id="Group_1858">
            <g id="Group_1857" transform="translate(0 0)">
                <path
                    id="Path_443"
                    d="M35.58 89.13H2.42A2.423 2.423 0 0 0 0 91.55v19.929a2.423 2.423 0 0 0 2.42 2.42h33.16a2.423 2.423 0 0 0 2.42-2.42V91.55a2.423 2.423 0 0 0-2.42-2.42zm.807 22.349a.808.808 0 0 1-.807.807H2.42a.808.808 0 0 1-.807-.807V91.55a.808.808 0 0 1 .807-.807h33.16a.808.808 0 0 1 .807.807z"
                    transform="translate(0 -89.13)"
                />
            </g>
        </g>
        <g id="Group_1860" transform="translate(21.863 7.079)">
            <g id="Group_1859" transform="translate(0 0)">
                <path
                    id="Path_444"
                    d="M304.037 184.506h-8.659a.807.807 0 0 0 0 1.614h8.659a.807.807 0 1 0 0-1.614z"
                    transform="translate(-294.571 -184.506)"
                />
            </g>
        </g>
        <g id="Group_1862" transform="translate(21.863 11.578)">
            <g id="Group_1861" transform="translate(0 0)">
                <path
                    id="Path_445"
                    d="M304.037 245.129h-8.659a.807.807 0 0 0 0 1.614h8.659a.807.807 0 1 0 0-1.614z"
                    transform="translate(-294.571 -245.129)"
                />
            </g>
        </g>
        <g id="Group_1864" transform="translate(21.863 16.077)">
            <g id="Group_1863" transform="translate(0 0)">
                <path
                    id="Path_446"
                    d="M304.037 305.752h-8.659a.807.807 0 1 0 0 1.614h8.659a.807.807 0 1 0 0-1.614z"
                    transform="translate(-294.571 -305.752)"
                />
            </g>
        </g>
    </>
);

const IdCardIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 49 49">
        <path id="Rectangle_682" fill="none" d="M0 0H49V49H0z" />
        <g id="id-card-user-svgrepo-com" transform="translate(5.5 12.115)">
            {sharedCardIcon}
            <g id="Group_1866" transform="translate(4.187 3.505)">
                <g id="Group_1865" transform="translate(0 0)">
                    <path
                        id="Path_447"
                        d="M67.127 147.775a1.916 1.916 0 0 1-1.892-1.537 4.627 4.627 0 0 0 1.314-1.717c.024 0 .048.005.072.005a.726.726 0 0 0 .573-.28 4.918 4.918 0 0 0 .992-3.182c-.078-2.45-1.783-4.381-3.883-4.4h-.15a3.684 3.684 0 0 0-1.6-.306A4 4 0 0 0 59.5 138a5.847 5.847 0 0 0-1.05 4.235 5.8 5.8 0 0 0 .468 1.625.726.726 0 0 0 .617.423.715.715 0 0 0 .213-.019 4.746 4.746 0 0 0 1.425 1.965 1.921 1.921 0 0 1-1.893 1.541 2.964 2.964 0 0 0-2.863 2.958v2.94a.726.726 0 0 0 .726.726h12.121a.726.726 0 0 0 .726-.726v-2.94a2.964 2.964 0 0 0-2.863-2.953zm-7.232-5.675l-.01-.067a4.242 4.242 0 0 1-.04-.477 4.162 4.162 0 0 1 .8-2.671 2.572 2.572 0 0 1 1.949-1.082 2.061 2.061 0 0 1 1.093.242.651.651 0 0 0 .345.076l.244-.011c1.3.009 2.4 1.353 2.455 3a3.865 3.865 0 0 1-.022.557 4.312 4.312 0 0 0-2.044-1.821.726.726 0 0 0-.962.417 3.126 3.126 0 0 1-.2.439 1.1 1.1 0 0 1-1.018.528h-.261a3.886 3.886 0 0 0-1.236.242 3.929 3.929 0 0 0-1.093.628zm1.012 1.054a2.909 2.909 0 0 1 .592-.325 2.552 2.552 0 0 1 .937-.15 2.555 2.555 0 0 0 2.264-1.152 3.028 3.028 0 0 1 .89 1.119c-.225 1.63-1.239 2.869-2.39 2.869-1.026.005-1.948-.966-2.293-2.357zm1.6 3.733a3.036 3.036 0 0 0 .7.082 3.089 3.089 0 0 0 .7-.082 3.341 3.341 0 0 0 1.37 1.8 2.2 2.2 0 0 1-4.14 0 3.341 3.341 0 0 0 1.363-1.797zm-4.635 6.057v-2.214a1.51 1.51 0 0 1 1.509-1.509c.017 0 .282-.015.395-.031a3.654 3.654 0 0 0 6.863 0c.114.015.378.031.395.031a1.51 1.51 0 0 1 1.509 1.509v2.214z"
                        transform="translate(-56.415 -136.354)"
                    />
                </g>
            </g>
        </g>
    </IconSvg>
);

const DrivingLicenceIcon: FC<IconSvgProps> = (props: IconSvgProps) => {
    const fill = props.fill ? props.fill : 'currentColor';

    return (
        <IconSvg {...props} viewBox="0 0 49 49">
            <defs>
                <style>
                    {`.cls-54{fill:none}.cls-54,.cls-6{stroke:${fill}}.cls-54{stroke-linecap:round;stroke-width:2.1px}.cls-6{fill:#fff}.cls-8{stroke:none}`}
                </style>
            </defs>
            <path id="Rectangle_682" d="M0 0H49V49H0z" fill="none" />
            <g id="id-card-user-svgrepo-com" transform="translate(5.5 12.115)">
                {sharedCardIcon}
                <g id="Group_2045" transform="translate(0 1.659)">
                    <g
                        id="Rectangle_752"
                        fill="none"
                        stroke={fill}
                        strokeWidth="1.4px"
                        transform="translate(4.5 8.885)"
                    >
                        <rect width="13" height="7" className="cls-8" rx="1" />
                        <rect width="11.6" height="5.6" x=".7" y=".7" fill="none" rx=".3" />
                    </g>
                    <g id="Path_552" fill="none">
                        <path
                            d="M1.782 0h5.484a1 1 0 0 1 1 1l.7 3.406a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1L.782 1a1 1 0 0 1 1-1z"
                            className="cls-8"
                            transform="translate(6.5 4.885)"
                        />
                        <path
                            fill={fill}
                            d="M2.126 1.4l-.628 2.606h5.953L6.918 1.4H2.126M1.782 0h5.484c.552 0 1 .448 1 1l.696 3.406c0 .553-.448 1-1 1h-7c-.552 0-1-.447-1-1L.782 1c0-.552.448-1 1-1z"
                            transform="translate(6.5 4.885)"
                        />
                    </g>
                    <path id="Line_495" d="M0 0L0 0.799" className="cls-54" transform="translate(7 15.768)" />
                    <path id="Line_496" d="M0 0L0 0.799" className="cls-54" transform="translate(15 15.768)" />
                    <g id="Ellipse_37" className="cls-6" transform="translate(6.5 11.567)">
                        <circle cx="1" cy="1" r="1" className="cls-8" />
                        <circle cx="1" cy="1" r=".5" fill="none" />
                    </g>
                    <g id="Ellipse_38" className="cls-6" transform="translate(13.5 11.567)">
                        <circle cx="1" cy="1" r="1" className="cls-8" />
                        <circle cx="1" cy="1" r=".5" fill="none" />
                    </g>
                    <path
                        id="Line_497"
                        fill="none"
                        stroke={fill}
                        strokeLinecap="round"
                        d="M0 0L2 0"
                        transform="translate(10 12.567)"
                    />
                </g>
            </g>
        </IconSvg>
    );
};

const ResidencePermitIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 49 49">
        <path id="Rectangle_682" fill="none" d="M0 0H49V49H0z" />
        <g id="id-card-user-svgrepo-com" transform="translate(3.736 9.813)">
            <g id="license-svgrepo-com-2" transform="translate(7.964 -2.825)">
                <g id="Group_2047" transform="translate(0 0)">
                    <g id="Group_2046" transform="translate(0 0)">
                        <path
                            id="Path_553"
                            d="M77.2 119.873a.985.985 0 0 0 .985.985h14.286a.985.985 0 0 0 0-1.969H78.187a.985.985 0 0 0-.987.984z"
                            transform="translate(-72.662 -105.882)"
                        />
                        <path
                            id="Path_554"
                            d="M78.187 152.507h14.284a.985.985 0 1 0 0-1.969H78.187a.985.985 0 1 0 0 1.969z"
                            transform="translate(-72.662 -134.069)"
                        />
                        <path
                            id="Path_555"
                            d="M78.187 44.507h4.851a.985.985 0 1 0 0-1.969h-4.851a.985.985 0 1 0 0 1.969z"
                            transform="translate(-72.662 -37.884)"
                        />
                        <path
                            id="Path_556"
                            d="M85.112 242.374h-6.925a.985.985 0 0 0 0 1.969h6.925a.985.985 0 0 0 0-1.969z"
                            transform="translate(-72.662 -215.859)"
                        />
                        <path
                            id="Path_557"
                            d="M85.112 214.512h-6.925a.985.985 0 1 0 0 1.969h6.925a.985.985 0 1 0 0-1.969z"
                            transform="translate(-72.662 -191.045)"
                        />
                        <path
                            id="Path_558"
                            d="M61.014 7.725L53.577.288a.985.985 0 0 0-.7-.288H36.688a.985.985 0 0 0-.988.985v31.441a.985.985 0 0 0 .985.985h23.63a.985.985 0 0 0 .985-.985V8.421a.985.985 0 0 0-.286-.696zm-7.367-4.582l4.513 4.513h-4.513V3.143zm5.686 28.3H37.672V1.969h14.006V8.64a.985.985 0 0 0 .985.985h6.671z"
                            transform="translate(-35.703 0)"
                        />
                        <path
                            id="Path_559"
                            d="M174.008 208.512a3.19 3.19 0 1 0 3.19 3.19 3.193 3.193 0 0 0-3.19-3.19zm0 4.411a1.221 1.221 0 1 1 1.221-1.221 1.222 1.222 0 0 1-1.221 1.221z"
                            transform="translate(-156.037 -185.701)"
                        />
                    </g>
                </g>
            </g>
        </g>
    </IconSvg>
);

const ImageIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M24.333 5.667v18.666H5.667V5.667h18.666m0-2.667H5.667A2.675 2.675 0 0 0 3 5.667v18.666A2.675 2.675 0 0 0 5.667 27h18.666A2.675 2.675 0 0 0 27 24.333V5.667A2.675 2.675 0 0 0 24.333 3zm-6.48 11.813l-4 5.16L11 16.52l-4 5.147h16l-5.147-6.853z"
            transform="translate(1 1)"
        />
    </IconSvg>
);

const LogoPrimaryIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 28 35">
        <defs>
            <clipPath id="clip-path">
                <path
                    id="Rectangle_768"
                    fill="#fff"
                    stroke="#707070"
                    d="M0 0H28V35H0z"
                    transform="translate(586.073 124.857)"
                />
            </clipPath>
            <style>{`.cls-3{fill:${brandA}}.cls-5{fill:#abd1ee}`}</style>
        </defs>
        <g id="Logo-Primary" clipPath="url(#clip-path)" transform="translate(-586.073 -124.857)">
            <g id="U6qZXw" transform="translate(582.94 125)">
                <g id="Group_2271" transform="translate(0 0)">
                    <g id="Group_2270">
                        <path
                            id="Path_576"
                            d="M153.671 150.83h-.282a6.4 6.4 0 0 0-.668.067 3.937 3.937 0 0 0-2.365 1.459 3.856 3.856 0 0 0-.856 2.416V154.969l.075.626c.008.056.011.112.023.167a4.074 4.074 0 0 0 1.128 1.993q1.675 1.681 3.353 3.359 1.586 1.588 3.174 3.173c.4.394.8.78 1.2 1.174q1.5 1.492 2.99 2.989c.035.035.072.069.116.11l.374-.353c.126-.118.254-.234.38-.353a.417.417 0 0 1 .729.2V174.709c0 .056.005.112.008.168l-.011.085-.056.023h-6.822a.482.482 0 0 1-.294-.088.4.4 0 0 1-.008-.634c.251-.252.5-.5.762-.757-.094-.1-.175-.187-.259-.271l-5.98-5.98c-1.206-1.2-2.418-2.4-3.623-3.61-.424-.424-.844-.853-1.246-1.3a14.4 14.4 0 0 1-1.007-1.214 8.65 8.65 0 0 1-.925-1.609 11.509 11.509 0 0 1-.468-1.163c-.165-.532-.3-1.075-.412-1.622a10.423 10.423 0 0 1-.154-1.245c-.023-.285-.007-.573 0-.859 0-.2.014-.392.027-.587.015-.237.036-.473.055-.709.013-.044.029-.088.038-.133.064-.32.118-.642.193-.959a10.209 10.209 0 0 1 .685-1.975 11 11 0 0 1 5.723-5.53c.315-.132.638-.244.957-.366l.047-.007c.135-.037.268-.08.4-.109l1.489-.3.052-.007.169-.009.113-.02.846-.038h.085l.253-.011h.018l.49.048h.226l.164.006.259.041 1.1.182.053.009c.044.015.087.034.132.045a10.94 10.94 0 0 1 6.44 4.464c.276.406.508.841.761 1.263l.008.027c.128.29.272.574.382.872.157.428.29.866.432 1.3l.056.226.186.991.01.08.006.113.026.2.018.144q0 .1.01.195v.141l.026.339v.507l-.026.339v.084c-.021.251-.031.5-.066.754a11.514 11.514 0 0 1-.411 1.868 9.952 9.952 0 0 1-.88 2.048 11.9 11.9 0 0 1-.772 1.2 17.292 17.292 0 0 1-1.166 1.365c-.5.535-1.043 1.038-1.563 1.559a1.508 1.508 0 0 0-.152.217l-4.945-4.945a1.449 1.449 0 0 0 .149-.112c.559-.549 1.12-1.1 1.673-1.653a4.175 4.175 0 0 0 .747-.982 4.019 4.019 0 0 0 .484-1.673 3.981 3.981 0 0 0-2.426-3.974 7.361 7.361 0 0 0-1.051-.291l-.083-.007-.171-.02z"
                            className="cls-3"
                            transform="translate(-136.33 -140.777)"
                        />
                        <path
                            id="Path_577"
                            d="M100.014 113.142v.254c.016.519.009 1.041.056 1.557a13.2 13.2 0 0 0 .236 1.59 11.847 11.847 0 0 0 1.238 3.256 11.988 11.988 0 0 0 1.931 2.6c.685.7 1.379 1.383 2.069 2.073l1.392 1.392q1.44 1.449 2.878 2.9 1.577 1.583 3.161 3.159a.821.821 0 0 1 .205.844.863.863 0 0 1-1.378.431c-.249-.2-.462-.454-.691-.683l-1.372-1.372-1.308-1.308-.763-.764-4.924-4.924c-.218-.218-.439-.434-.645-.663a12.334 12.334 0 0 1-1.29-1.612c-.318-.5-.647-1-.931-1.519a13.066 13.066 0 0 1-1.092-2.755 14.015 14.015 0 0 1-.5-3.133v-.141-.93-.282l.033-.348c.039-.375.061-.753.122-1.124a12.487 12.487 0 0 1 .511-2.125c.169-.482.33-.969.539-1.433a12.07 12.07 0 0 1 1.256-2.212c.377-.512.762-1.02 1.18-1.5a13.039 13.039 0 0 1 1.073-1.043c.311-.283.635-.553.971-.8.315-.236.649-.447.982-.657.267-.168.545-.318.817-.476l.168-.087.109-.054.283-.14.166-.068.144-.073.477-.2.059-.024.279-.1.089-.041.138-.042.032-.021.3-.092.4-.132.9-.227.282-.058.765-.13.229-.036.331-.032h.113c.088-.007.176-.017.264-.022.316-.015.632-.028.948-.041h.112c.247.013.5.018.742.041.54.05 1.082.089 1.617.169a13.024 13.024 0 0 1 3.583 1.111 14.06 14.06 0 0 1 4.705 3.569c.222.26.434.528.651.793l.142.2.061.087.09.136.159.229.31.487.145.248.079.139.092.168.4.787.034.084.171.395.218.537.283.845.057.2.253 1.043.025.117.037.25.022.085.037.284.019.028v.083l.032.284.02.063.064 1.063c.007.237.016.474.018.71 0 .1-.012.2-.018.3-.011.163-.018.326-.033.489-.037.4-.059.811-.12 1.212a12.015 12.015 0 0 1-.611 2.432c-.211.558-.43 1.116-.69 1.652a12.094 12.094 0 0 1-1.476 2.344c-.328.4-.663.8-1.02 1.18-.459.487-.945.948-1.415 1.425a.783.783 0 0 1-.844.217.864.864 0 0 1-.371-1.422c.426-.453.87-.889 1.3-1.337.233-.242.467-.485.683-.743.253-.3.5-.612.725-.937a12 12 0 0 0 1.075-1.905 11.013 11.013 0 0 0 .656-1.84 12.029 12.029 0 0 0 .41-2.371l.009-.619.021-.142v-.226l-.023-.113-.005-.14v-.141-.2c-.017-.21-.032-.42-.052-.629a11.338 11.338 0 0 0-.521-2.454 12.129 12.129 0 0 0-1.72-3.386c-.209-.288-.443-.557-.665-.834-.306-.326-.606-.658-.92-.976a5.7 5.7 0 0 0-.459-.381.405.405 0 0 0-.057-.076 12.191 12.191 0 0 0-4.28-2.335c-.428-.131-.871-.215-1.307-.321l-.194-.035-.877-.13-.224-.012-.566-.044H111.989l-.678.046-.2.011-1.048.155-.166.034-.794.19a.025.025 0 0 0-.022.006c-.041.008-.083.012-.124.024a12.133 12.133 0 0 0-3.515 1.644 12.427 12.427 0 0 0-4.606 5.761 11.111 11.111 0 0 0-.576 1.966v.056l-.165 1.015-.033.3q-.022.209-.048.423z"
                            className="cls-3"
                            transform="translate(-95.189 -99.767)"
                        />
                        <path
                            id="Path_578"
                            d="M163.593 504.387v-4.117-.37-.022a.714.714 0 0 1 .092-.164c.409-.415.826-.822 1.232-1.24.1-.107.158-.092.256.006q2.317 2.326 4.641 4.646c.032.032.062.067.1.107-.041.043-.076.081-.112.117-.418.419-.839.836-1.253 1.258a.258.258 0 0 1-.2.089h-4.719l-.022-.025-.015-.285z"
                            className="cls-3"
                            transform="translate(-155.922 -470.485)"
                        />
                        <path
                            id="Path_579"
                            fill="#89c0ea"
                            d="M434.6 587.9l-.056.023z"
                            transform="translate(-407.894 -553.716)"
                        />
                        <path
                            id="Path_580"
                            d="M163.714 587.88l-.022-.025z"
                            className="cls-5"
                            transform="translate(-156.014 -553.675)"
                        />
                        <path
                            id="Path_581"
                            d="M163.366 519.519l-.018-.011.018-.011v.022z"
                            className="cls-5"
                            transform="translate(-155.694 -490.104)"
                        />
                        <path
                            id="Path_582"
                            fill="#8cc2e9"
                            d="M54.113 706.137l-.056.056v-.066z"
                            transform="translate(-54.056 -663.664)"
                        />
                    </g>
                </g>
            </g>
        </g>
    </IconSvg>
);

const LogoNegativeIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 28 35">
        <defs>
            <clipPath id="clip-path">
                <path
                    id="Rectangle_769"
                    fill="#fff"
                    stroke="#707070"
                    d="M0 0H28V35H0z"
                    transform="translate(586.073 124.857)"
                />
            </clipPath>
            <style>{`.cls-72{fill:#fff}.cls-57{fill:#abd1ee}`}</style>
        </defs>
        <g id="Logo-Negative" clipPath="url(#clip-path)" transform="translate(-586.073 -124.857)">
            <g id="U6qZXw" transform="translate(582.94 125)">
                <g id="Group_2274" transform="translate(0 0)">
                    <g id="Group_2273">
                        <path
                            id="Path_583"
                            d="M153.671 150.83h-.282a6.4 6.4 0 0 0-.668.067 3.937 3.937 0 0 0-2.365 1.459 3.856 3.856 0 0 0-.856 2.416V154.969l.075.626c.008.056.011.112.023.167a4.074 4.074 0 0 0 1.128 1.993q1.675 1.681 3.353 3.359 1.586 1.588 3.174 3.173c.4.394.8.78 1.2 1.174q1.5 1.492 2.99 2.989c.035.035.072.069.116.11l.374-.353c.126-.118.254-.234.38-.353a.417.417 0 0 1 .729.2V174.709c0 .056.005.112.008.168l-.011.085-.056.023h-6.822a.482.482 0 0 1-.294-.088.4.4 0 0 1-.008-.634c.251-.252.5-.5.762-.757-.094-.1-.175-.187-.259-.271l-5.98-5.98c-1.206-1.2-2.418-2.4-3.623-3.61-.424-.424-.844-.853-1.246-1.3a14.4 14.4 0 0 1-1.007-1.214 8.65 8.65 0 0 1-.925-1.609 11.509 11.509 0 0 1-.468-1.163c-.165-.532-.3-1.075-.412-1.622a10.423 10.423 0 0 1-.154-1.245c-.023-.285-.007-.573 0-.859 0-.2.014-.392.027-.587.015-.237.036-.473.055-.709.013-.044.029-.088.038-.133.064-.32.118-.642.193-.959a10.209 10.209 0 0 1 .685-1.975 11 11 0 0 1 5.723-5.53c.315-.132.638-.244.957-.366l.047-.007c.135-.037.268-.08.4-.109l1.489-.3.052-.007.169-.009.113-.02.846-.038h.085l.253-.011h.018l.49.048h.226l.164.006.259.041 1.1.182.053.009c.044.015.087.034.132.045a10.94 10.94 0 0 1 6.44 4.464c.276.406.508.841.761 1.263l.008.027c.128.29.272.574.382.872.157.428.29.866.432 1.3l.056.226.186.991.01.08.006.113.026.2.018.144q0 .1.01.195v.141l.026.339v.507l-.026.339v.084c-.021.251-.031.5-.066.754a11.514 11.514 0 0 1-.411 1.868 9.952 9.952 0 0 1-.88 2.048 11.9 11.9 0 0 1-.772 1.2 17.292 17.292 0 0 1-1.166 1.365c-.5.535-1.043 1.038-1.563 1.559a1.508 1.508 0 0 0-.152.217l-4.945-4.945a1.449 1.449 0 0 0 .149-.112c.559-.549 1.12-1.1 1.673-1.653a4.175 4.175 0 0 0 .747-.982 4.019 4.019 0 0 0 .484-1.673 3.981 3.981 0 0 0-2.426-3.974 7.361 7.361 0 0 0-1.051-.291l-.083-.007-.171-.02z"
                            className="cls-72"
                            transform="translate(-136.33 -140.777)"
                        />
                        <path
                            id="Path_584"
                            d="M100.014 113.142v.254c.016.519.009 1.041.056 1.557a13.2 13.2 0 0 0 .236 1.59 11.847 11.847 0 0 0 1.238 3.256 11.988 11.988 0 0 0 1.931 2.6c.685.7 1.379 1.383 2.069 2.073l1.392 1.392q1.44 1.449 2.878 2.9 1.577 1.583 3.161 3.159a.821.821 0 0 1 .205.844.863.863 0 0 1-1.378.431c-.249-.2-.462-.454-.691-.683l-1.372-1.372-1.308-1.308-.763-.764-4.924-4.924c-.218-.218-.439-.434-.645-.663a12.334 12.334 0 0 1-1.29-1.612c-.318-.5-.647-1-.931-1.519a13.066 13.066 0 0 1-1.092-2.755 14.015 14.015 0 0 1-.5-3.133v-.141-.93-.282l.033-.348c.039-.375.061-.753.122-1.124a12.487 12.487 0 0 1 .511-2.125c.169-.482.33-.969.539-1.433a12.07 12.07 0 0 1 1.256-2.212c.377-.512.762-1.02 1.18-1.5a13.039 13.039 0 0 1 1.073-1.043c.311-.283.635-.553.971-.8.315-.236.649-.447.982-.657.267-.168.545-.318.817-.476l.168-.087.109-.054.283-.14.166-.068.144-.073.477-.2.059-.024.279-.1.089-.041.138-.042.032-.021.3-.092.4-.132.9-.227.282-.058.765-.13.229-.036.331-.032h.113c.088-.007.176-.017.264-.022.316-.015.632-.028.948-.041h.112c.247.013.5.018.742.041.54.05 1.082.089 1.617.169a13.024 13.024 0 0 1 3.583 1.111 14.06 14.06 0 0 1 4.705 3.569c.222.26.434.528.651.793l.142.2.061.087.09.136.159.229.31.487.145.248.079.139.092.168.4.787.034.084.171.395.218.537.283.845.057.2.253 1.043.025.117.037.25.022.085.037.284.019.028v.083l.032.284.02.063.064 1.063c.007.237.016.474.018.71 0 .1-.012.2-.018.3-.011.163-.018.326-.033.489-.037.4-.059.811-.12 1.212a12.015 12.015 0 0 1-.611 2.432c-.211.558-.43 1.116-.69 1.652a12.094 12.094 0 0 1-1.476 2.344c-.328.4-.663.8-1.02 1.18-.459.487-.945.948-1.415 1.425a.783.783 0 0 1-.844.217.864.864 0 0 1-.371-1.422c.426-.453.87-.889 1.3-1.337.233-.242.467-.485.683-.743.253-.3.5-.612.725-.937a12 12 0 0 0 1.075-1.905 11.013 11.013 0 0 0 .656-1.84 12.029 12.029 0 0 0 .41-2.371l.009-.619.021-.142v-.226l-.023-.113-.005-.14v-.141-.2c-.017-.21-.032-.42-.052-.629a11.338 11.338 0 0 0-.521-2.454 12.129 12.129 0 0 0-1.72-3.386c-.209-.288-.443-.557-.665-.834-.306-.326-.606-.658-.92-.976a5.7 5.7 0 0 0-.459-.381.405.405 0 0 0-.057-.076 12.191 12.191 0 0 0-4.28-2.335c-.428-.131-.871-.215-1.307-.321l-.194-.035-.877-.13-.224-.012-.566-.044H111.989l-.678.046-.2.011-1.048.155-.166.034-.794.19a.025.025 0 0 0-.022.006c-.041.008-.083.012-.124.024a12.133 12.133 0 0 0-3.515 1.644 12.427 12.427 0 0 0-4.606 5.761 11.111 11.111 0 0 0-.576 1.966v.056l-.165 1.015-.033.3q-.022.209-.048.423z"
                            className="cls-72"
                            transform="translate(-95.189 -99.767)"
                        />
                        <path
                            id="Path_585"
                            d="M163.593 504.387v-4.117-.37-.022a.714.714 0 0 1 .092-.164c.409-.415.826-.822 1.232-1.24.1-.107.158-.092.256.006q2.317 2.326 4.641 4.646c.032.032.062.067.1.107-.041.043-.076.081-.112.117-.418.419-.839.836-1.253 1.258a.258.258 0 0 1-.2.089h-4.719l-.022-.025-.015-.285z"
                            className="cls-72"
                            transform="translate(-155.922 -470.485)"
                        />
                        <path
                            id="Path_586"
                            fill="#89c0ea"
                            d="M434.6 587.9l-.056.023z"
                            transform="translate(-407.894 -553.716)"
                        />
                        <path
                            id="Path_587"
                            d="M163.714 587.88l-.022-.025z"
                            className="cls-57"
                            transform="translate(-156.014 -553.675)"
                        />
                        <path
                            id="Path_588"
                            d="M163.366 519.519l-.018-.011.018-.011v.022z"
                            className="cls-57"
                            transform="translate(-155.694 -490.104)"
                        />
                        <path
                            id="Path_589"
                            fill="#8cc2e9"
                            d="M54.113 706.137l-.056.056v-.066z"
                            transform="translate(-54.056 -663.664)"
                        />
                    </g>
                </g>
            </g>
        </g>
    </IconSvg>
);

const RightArrowIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M11.093 6L8.61 8.483l8.067 8.084-8.067 8.085 2.483 2.483 10.568-10.567z"
            transform="translate(1.284 -0.568)"
        />
    </IconSvg>
);

const SearchIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M19.667 17.667h-1.054l-.373-.36a8.68 8.68 0 1 0-.933.933l.36.373v1.053l6.667 6.653 1.987-1.987zm-8 0a6 6 0 1 1 6-6 5.992 5.992 0 0 1-6 6z"
            transform="translate(1 1)"
        />
    </IconSvg>
);

const ShowIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M15.667 4.5A15.769 15.769 0 0 0 1 14.5a15.756 15.756 0 0 0 29.333 0 15.769 15.769 0 0 0-14.666-10zm0 16.667a6.667 6.667 0 1 1 6.666-6.667 6.669 6.669 0 0 1-6.666 6.667zm0-10.667a4 4 0 1 0 4 4 3.995 3.995 0 0 0-4-4z"
            transform="translate(0.333 1.5)"
        />
    </IconSvg>
);

const TickIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path d="M10.867 19.733l-5.6-5.6L3.4 16l7.467 7.467 16-16L25 5.6z" transform="translate(1.133 1.867)" />
    </IconSvg>
);

const UploadIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <g>
            <path fill="none" d="M0 0H32V32H0z" />
        </g>
        <g>
            <path
                d="M22.667 18.667v4h-16v-4H4v4a2.675 2.675 0 0 0 2.667 2.667h16a2.675 2.675 0 0 0 2.667-2.667v-4zM8 10.667l1.88 1.88 3.453-3.44V20H16V9.107l3.453 3.44 1.88-1.88L14.667 4z"
                transform="translate(5.333 5.333) translate(-4 -4)"
            />
        </g>
    </IconSvg>
);

const PartnershipIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path d="M3.87 5.266A8.709 8.709 0 0 1 16 4.8a8.709 8.709 0 0 1 12.12.459 9.45 9.45 0 0 1 .48 12.529L17.941 28.84a2.68 2.68 0 0 1-3.731.145l-.151-.143L3.4 17.788a9.45 9.45 0 0 1 .47-12.522zm1.94 2.006a6.541 6.541 0 0 0-.2 8.81l.2.218L16 26.835l7.278-7.525-4.852-5.01-1.455 1.5a4.023 4.023 0 0 1-5.823 0 4.359 4.359 0 0 1 0-6.02l2.884-2.98a6.025 6.025 0 0 0-8.01.268l-.211.207zm11.646 4.012a1.341 1.341 0 0 1 1.94 0l5.822 6.016.972-1a6.539 6.539 0 0 0 .1-8.923 6.033 6.033 0 0 0-8.627-.313l-.21.207-4.367 4.514a1.454 1.454 0 0 0-.107 1.883l.107.123a1.339 1.339 0 0 0 1.821.111l.119-.111z" />
    </IconSvg>
);

const WatchlistIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path id="Rectangle_682" d="M0 0H41V41H0z" fill="none" />
        <g id="checklist_black_24dp" transform="translate(8.5 8.5)">
            <path id="Rectangle_703" d="M0 0H24V24H0z" fill="none" />
            <path
                id="Path_494"
                d="M22 7h-9v2h9zm0 8h-9v2h9zM5.54 11L2 7.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41zm0 8L2 15.46l1.41-1.41 2.12 2.12 4.24-4.24 1.41 1.41z"
            />
        </g>
    </IconSvg>
);

const CheckboxIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 41 41">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            fill="#494646"
            d="M10.867 19.733l-5.6-5.6L3.4 16l7.467 7.467 16-16L25 5.6z"
            transform="translate(1.133 1.867)"
        />
    </IconSvg>
);

const RadioButtonSelectedEnabledIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 30 30">
        <path id="Rectangle_757" d="M0 0H30V30H0z" fill="none" />
        <path id="Path_274" d="M0 0h17v17H0z" fill="none" transform="translate(5.395 5)" />
        <path
            id="Path_275"
            fill="#2172ab"
            d="M12 7a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0-5a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"
            transform="translate(3.395 3)"
        />
    </IconSvg>
);

const RadioButtonUnSelectedEnabledIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 30 30">
        <path id="Padding" d="M0 0H30V30H0z" fill="none" />
        <path id="Path_272" d="M0 0h17v17H0z" fill="none" transform="translate(6.988 6.593)" />
        <path
            id="Path_273"
            fill="#5e5a5a"
            d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8z"
            transform="translate(3.395 3)"
        />
    </IconSvg>
);

const SwitchUnselectedEnabledIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 60 30.5">
        <defs>
            <filter id="Rectangle" width="29" height="29" x="8.5" y="1.5" filterUnits="userSpaceOnUse">
                <feOffset dy="1" />
                <feGaussianBlur result="blur" stdDeviation="1.5" />
                <feFlood floodOpacity=".2" />
                <feComposite in2="blur" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <filter id="Rectangle-2" width="23" height="23.5" x="11.5" y="5" filterUnits="userSpaceOnUse">
                <feOffset dy="2" />
                <feGaussianBlur result="blur-2" stdDeviation=".5" />
                <feFlood floodOpacity=".122" />
                <feComposite in2="blur-2" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <filter id="Rectangle-3" width="23" height="23" x="11.5" y="4.5" filterUnits="userSpaceOnUse">
                <feOffset dy="1" />
                <feGaussianBlur result="blur-3" stdDeviation=".5" />
                <feFlood floodOpacity=".141" />
                <feComposite in2="blur-3" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <style>{`.cls-46{fill:#fff}.cls-52{mix-blend-mode:multiply;isolation:isolate}`}</style>
        </defs>
        <g id="Light_Selection_Controls_3._Switch_2_States_" transform="translate(13 5)">
            <rect
                id="_Color_l_Track"
                width="34"
                height="14"
                fill="rgba(0,0,0,0.38)"
                rx="7"
                transform="translate(0 3)"
            />
            <g id="Light_Elevation_00dp">
                <g id="Shadow" className="cls-52">
                    <g filter="url(#Rectangle)" transform="matrix(1, 0, 0, 1, -13, -5)">
                        <rect
                            id="Rectangle-4"
                            width="20"
                            height="20"
                            className="cls-46"
                            rx="10"
                            transform="translate(13 5)"
                        />
                    </g>
                    <g filter="url(#Rectangle-2)" transform="matrix(1, 0, 0, 1, -13, -5)">
                        <rect
                            id="Rectangle-5"
                            width="20"
                            height="20"
                            className="cls-46"
                            rx="10"
                            transform="translate(13 5)"
                        />
                    </g>
                    <g filter="url(#Rectangle-3)" transform="matrix(1, 0, 0, 1, -13, -5)">
                        <rect
                            id="Rectangle-6"
                            width="20"
                            height="20"
                            className="cls-46"
                            rx="10"
                            transform="translate(13 5)"
                        />
                    </g>
                </g>
            </g>
            <circle id="_Color_l_Thumb" cx="10" cy="10" r="10" className="cls-46" />
        </g>
        <path id="Padding" fill="none" d="M0 0H60V30H0z" />
    </IconSvg>
);

const SwitchSelectedEnabledIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 60 30.5">
        <defs>
            <filter id="Rectangle" width="29" height="29" x="22.5" y="1.5" filterUnits="userSpaceOnUse">
                <feOffset dy="1" />
                <feGaussianBlur result="blur" stdDeviation="1.5" />
                <feFlood floodOpacity=".2" />
                <feComposite in2="blur" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <filter id="Rectangle-2" width="23" height="23.5" x="25.5" y="5" filterUnits="userSpaceOnUse">
                <feOffset dy="2" />
                <feGaussianBlur result="blur-2" stdDeviation=".5" />
                <feFlood floodOpacity=".122" />
                <feComposite in2="blur-2" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <filter id="Rectangle-3" width="23" height="23" x="25.5" y="4.5" filterUnits="userSpaceOnUse">
                <feOffset dy="1" />
                <feGaussianBlur result="blur-3" stdDeviation=".5" />
                <feFlood floodOpacity=".141" />
                <feComposite in2="blur-3" operator="in" />
                <feComposite in="SourceGraphic" />
            </filter>
            <style>{`.cls-19{fill:#fff}.cls-23{mix-blend-mode:multiply;isolation:isolate}`}</style>
        </defs>
        <g id="Light_Selection_Controls_3._Switch_2_States_" transform="translate(13 5)">
            <rect id="_Color_l_Track" width="34" height="14" fill="#5099ce" rx="7" transform="translate(0 3)" />
            <g id="Light_Elevation_00dp" transform="translate(14)">
                <g id="Shadow" className="cls-23">
                    <g filter="url(#Rectangle)" transform="matrix(1, 0, 0, 1, -27, -5)">
                        <rect
                            id="Rectangle-4"
                            width="20"
                            height="20"
                            className="cls-19"
                            rx="10"
                            transform="translate(27 5)"
                        />
                    </g>
                    <g filter="url(#Rectangle-2)" transform="matrix(1, 0, 0, 1, -27, -5)">
                        <rect
                            id="Rectangle-5"
                            width="20"
                            height="20"
                            className="cls-19"
                            rx="10"
                            transform="translate(27 5)"
                        />
                    </g>
                    <g filter="url(#Rectangle-3)" transform="matrix(1, 0, 0, 1, -27, -5)">
                        <rect
                            id="Rectangle-6"
                            width="20"
                            height="20"
                            className="cls-"
                            rx="10"
                            transform="translate(27 5)"
                        />
                    </g>
                </g>
            </g>
            <path
                id="_Color_l_Thumb"
                fill="#2172ab"
                d="M10 0A10 10 0 1 1 0 10 10 10 0 0 1 10 0z"
                transform="translate(14)"
            />
        </g>
        <path id="Padding" fill="none" d="M0 0H60V30H0z" />
    </IconSvg>
);

const DownIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <g>
            <path fill="none" d="M0 0h32v32H0z" transform="translate(32) rotate(90)" />
            <path
                d="M2.483 0L0 2.483l8.067 8.084L0 18.652l2.483 2.483 10.568-10.567z"
                transform="translate(32) rotate(90) translate(9.894 5.432)"
            />
        </g>
    </IconSvg>
);

const LeftIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32.002 32">
        <path fill="none" d="M32 0H0v32h32z" />
        <path
            d="M19.178 6l2.484 2.483-8.067 8.084 8.067 8.084-2.484 2.483L8.61 16.568z"
            transform="translate(0.446 -0.567)"
        />
    </IconSvg>
);

const DropdownIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M13.53 6.773L17.757 11l1.88-1.88L13.53 3 7.41 9.12 9.3 11zm0 16.453L9.3 19l-1.88 1.88L13.53 27l6.12-6.12L17.757 19z"
            transform="translate(2.47 1)"
        />
    </IconSvg>
);

export const IconDropdownArrow = () =>
    'data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4KPHN2ZyB3aWR0aD0iN3B4IiBoZWlnaHQ9IjE3cHgiIHZpZXdCb3g9IjAgMCA3IDE3IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPgogICAgPHRpdGxlPlNlbGVjdG9yIENvcHk8L3RpdGxlPgogICAgPGcgaWQ9Imkt4oCULVN5bWJvbHMiIHN0cm9rZT0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIxIiBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxnIGlkPSJTZWxlY3Rvci1Db3B5IiBmaWxsPSIjMjEyQjMyIj4KICAgICAgICAgICAgPHBvbHlnb24gaWQ9IlRyaWFuZ2xlIiBwb2ludHM9IjMuNSAwIDcgNyAwIDciPjwvcG9seWdvbj4KICAgICAgICAgICAgPHBvbHlnb24gaWQ9IlRyaWFuZ2xlIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgzLjUsIDEzLjUpIHJvdGF0ZSgtMTgwKSB0cmFuc2xhdGUoLTMuNSwgLTEzLjUpIiBwb2ludHM9IjMuNSAxMCA3IDE3IDAgMTciPjwvcG9seWdvbj4KICAgICAgICA8L2c+CiAgICA8L2c+Cjwvc3ZnPg==';

const AddCircleIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 0 32 32">
        <path fill="none" d="M0 0h32v32H0z" />
        <path
            d="M16.667 8.667H14V14H8.667v2.667H14V22h2.667v-5.333H22V14h-5.333zM15.333 2a13.333 13.333 0 1 0 13.334 13.333A13.338 13.338 0 0 0 15.333 2zm0 24A10.667 10.667 0 1 1 26 15.333 10.681 10.681 0 0 1 15.333 26z"
            transform="translate(0.667 0.667)"
        />
    </IconSvg>
);

const QuestionCircleIcon: FC<IconSvgProps> = (props: IconSvgProps) => (
    <IconSvg {...props} viewBox="0 -960 960 960">
        <path d="M478-240q21 0 35.5-14.5T528-290q0-21-14.5-35.5T478-340q-21 0-35.5 14.5T428-290q0 21 14.5 35.5T478-240Zm-36-154h74q0-33 7.5-52t42.5-52q26-26 41-49.5t15-56.5q0-56-41-86t-97-30q-57 0-92.5 30T342-618l66 26q5-18 22.5-39t53.5-21q32 0 48 17.5t16 38.5q0 20-12 37.5T506-526q-44 39-54 59t-10 73Zm38 314q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
    </IconSvg>
);

export const ICON_MAP = {
    applicationComplete: ApplicationCompleteIcon,
    backArrow: BackArrowIcon,
    comment: CommentIcon,
    council: CouncilIcon,
    creation: CreationIcon,
    cross: CrossIcon,
    document: DocumentIcon,
    donated: DonatedIcon,
    download: DownloadIcon,
    emailSent: EmailSentIcon,
    hide: HideIcon,
    idCheck: IdCheckIcon,
    identityReport: IdentityReportIcon,
    image: ImageIcon,
    logoNegative: LogoNegativeIcon,
    logoPrimary: LogoPrimaryIcon,
    rightArrow: RightArrowIcon,
    search: SearchIcon,
    show: ShowIcon,
    tick: TickIcon,
    upload: UploadIcon,
    partnership: PartnershipIcon,
    partner: PartnershipIcon,
    watchlist: WatchlistIcon,
    passport: PassportIcon,
    idCard: IdCardIcon,
    residencePermit: ResidencePermitIcon,
    drivingLicence: DrivingLicenceIcon,
    checkbox: CheckboxIcon,
    radioButtonSelectedEnabled: RadioButtonSelectedEnabledIcon,
    radioButtonUnSelectedEnabled: RadioButtonUnSelectedEnabledIcon,
    switchUnselectedEnabled: SwitchUnselectedEnabledIcon,
    switchSelectedEnabled: SwitchSelectedEnabledIcon,
    down: DownIcon,
    leftIcon: LeftIcon,
    dropdown: DropdownIcon,
    addCircle: AddCircleIcon,
    question: QuestionCircleIcon,
};
